/**
* @name: EmailLeftNav
* @author: weiyong
* @date: 2026-01-22 17:08
* @description：邮件左侧导航组件
* @update: 2026-01-22 17:08
*/
<template>
  <div class="email-left-nav">
    <div class="nav-header">
      <a-button type="primary" size="large" block @click="handleCompose">
        <span class="remix-icon ri-mail-send-line"></span>
        {{ t('写信') }}
      </a-button>
    </div>
    
    <div class="nav-menu">
      <a-menu
        v-model:selectedKeys="selectedKeys"
        v-model:openKeys="openKeys"
        mode="inline"
        :inline-collapsed="false"
        @select="handleMenuSelect"
        @openChange="handleOpenChange"
      >
        <a-menu-item key="EmailInbox">
          <span class="remix-icon ri-inbox-line"></span>
          <span>{{ t('收件箱') }}</span>
        </a-menu-item>
        <a-menu-item key="EmailSent">
          <span class="remix-icon ri-send-plane-line"></span>
          <span>{{ t('已发送') }}</span>
        </a-menu-item>
        <a-menu-item key="EmailDrafts">
          <span class="remix-icon ri-draft-line"></span>
          <span>{{ t('草稿箱') }}</span>
        </a-menu-item>
        <a-menu-item key="EmailTrash">
         <div class="w100b flex-between" style="display: flex;justify-content: space-between;align-items: center;">
           <div>
            <span class="remix-icon ri-delete-bin-line"></span>
          <span>{{ t('垃圾箱') }}</span>
           </div>
          <a-tooltip :title="t('点击清空垃圾箱')" placement="right">
            <span 
              class="trash-clear-icon remix-icon ri-delete-bin-line" 
              @click.stop="handleClearTrash"
            ></span>
          </a-tooltip>
         </div>
        </a-menu-item>
        <a-menu-item key="todo">
          <span class="remix-icon ri-todo-line"></span>
          <span>{{ t('待办邮件') }}</span>
        </a-menu-item>
        <a-menu-item key="starred">
          <span class="remix-icon ri-star-line"></span>
          <span>{{ t('标星邮件') }}</span>
        </a-menu-item>
        <a-menu-item key="all">
          <span class="remix-icon ri-mail-line"></span>
          <span>{{ t('所有邮件') }}</span>
        </a-menu-item>
        
        <a-menu-divider />
        
        <!-- 我的文件夹 - 可展开子菜单 -->
        <a-sub-menu key="myFolders">
          <template #title>
            <span class="remix-icon ri-folder-2-line"></span>
            <span>{{ t('我的文件夹') }}</span>
          </template>
          <template v-if="foldersLoading">
            <a-menu-item key="folders-loading" disabled>
              <span class="remix-icon ri-loader-4-line" style="animation: spin 1s linear infinite;"></span>
              <span>加载中...</span>
            </a-menu-item>
          </template>
          <template v-else-if="folderList.length === 0">
            <a-menu-item key="folders-empty" disabled>
              <span style="color: #999;">暂无文件夹</span>
            </a-menu-item>
          </template>
          <template v-else>
            <a-menu-item
              v-for="folder in folderList"
              :key="`folder-${folder.id}`"
            >
              <span class="remix-icon ri-folder-line"></span>
              <span>{{ folder.folderName }}</span>
              <span v-if="folder.unreadCount" class="folder-badge">{{ folder.unreadCount }}</span>
            </a-menu-item>
          </template>
        </a-sub-menu>
        
        <!-- 我的标签 - 可展开子菜单 -->
        <a-sub-menu key="myTags">
          <template #title>
            <span class="remix-icon ri-price-tag-3-line"></span>
            <span>{{ t('我的标签') }}</span>
          </template>
          <template v-if="tagsLoading">
            <a-menu-item key="tags-loading" disabled>
              <span class="remix-icon ri-loader-4-line" style="animation: spin 1s linear infinite;"></span>
              <span>加载中...</span>
            </a-menu-item>
          </template>
          <template v-else-if="tagList.length === 0">
            <a-menu-item key="tags-empty" disabled>
              <span style="color: #999;">暂无标签</span>
            </a-menu-item>
          </template>
          <template v-else>
            <a-menu-item
              v-for="tag in tagList"
              :key="`tag-${tag.id}`"
            >
              <span 
                class="tag-color-dot" 
                :style="{ backgroundColor: tag.labelColor || '#1890ff' }"
              ></span>
              <span>{{ tag.labelName }}</span>
              <span v-if="tag.emailCount" class="tag-badge">{{ tag.emailCount }}</span>
            </a-menu-item>
          </template>
        </a-sub-menu>
        
        <a-menu-divider />
        
        <a-menu-item key="EmailAttachment">
          <span class="remix-icon ri-attachment-line"></span>
          <span>{{ t('附件中心') }}</span>
        </a-menu-item>
        <a-menu-item key="EmailSettings">
          <span class="remix-icon ri-list-settings-line"></span>
          <span>{{ t('邮件设置') }}</span>
        </a-menu-item>
        <template v-if="isGroupAdmin">
          <a-menu-item key="EmailMonitor">
            <span class="remix-icon ri-dashboard-line"></span>
            <span>{{ t('邮件监控') }}</span>
          </a-menu-item>
        </template>
      </a-menu>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';

export default defineComponent({
  name: "EmailLeftNav",
});
</script>

<script lang="ts" setup>
import {ref, watch, onMounted, computed, nextTick} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRouter, useRoute} from 'vue-router';
import {Button as AButton, Menu as AMenu, MenuItem as AMenuItem, MenuDivider as AMenuDivider, SubMenu as ASubMenu, Tooltip as ATooltip, Modal, message} from 'ant-design-vue';
import NProgress from 'nprogress';
import GoingUtils from '@/utils/goingUtils'; // 导入GoingUtils
import {useEmailDataStore} from '../store/useEmailDataStore'; // 导入邮件数据store
import * as emailApi from '../api/emailApi'; // 导入邮件API

const {t} = useI18n();
const router = useRouter();
const route = useRoute();

// 使用邮件数据store
const emailDataStore = useEmailDataStore();

// 检查当前用户是否为groupAdmin
const isGroupAdmin = computed(() => {
  return GoingUtils.isGroupAdmin();
});

// 选中的菜单项，默认选中当前路由
const selectedKeys = ref<string[]>([]);

// 打开的子菜单键
const openKeys = ref<string[]>([]);

// 初始化选中状态
const initSelectedKeys = () => {
  // 如果是文件夹页面，根据 folderId 设置选中项
  if (route.name === 'EmailFolder' && route.query.folderId) {
    selectedKeys.value = [`folder-${route.query.folderId}`];
    // 展开"我的文件夹"子菜单，确保一定展开
    if (!openKeys.value.includes('myFolders')) {
      openKeys.value = [...openKeys.value.filter(k => k !== 'myTags'), 'myFolders'];
    }
  } else if (route.name === 'EmailLabel' && route.query.labelId) {
    // 如果是标签页面，根据 labelId 设置选中项
    selectedKeys.value = [`tag-${route.query.labelId}`];
    // 展开"我的标签"子菜单，确保一定展开
    if (!openKeys.value.includes('myTags')) {
      openKeys.value = [...openKeys.value.filter(k => k !== 'myFolders'), 'myTags'];
    }
  } else {
    // 其他页面，使用路由名称
    selectedKeys.value = [route.name as string || 'EmailCompose'];
    // 清空子菜单展开状态
    // openKeys.value = [];
  }
};

// 文件夹列表 - 从 store 读取，并过滤掉系统内置文件夹（如果 API 返回了它们）
const folderList = computed(() => {
  const list = emailDataStore.folders || [];
  // 过滤掉名称为收件箱、已发送、草稿箱、垃圾箱的文件夹，避免重复显示
  const systemFolderNames = [t('收件箱'), t('已发送'), t('草稿箱'), t('垃圾箱'), 'Inbox', 'Sent', 'Drafts', 'Trash'];
  return list.filter(folder => !systemFolderNames.includes(folder.folderName));
});
const foldersLoading = computed(() => emailDataStore.foldersLoading);

// 标签列表 - 从 store 读取
const tagList = computed(() => emailDataStore.labels);
const tagsLoading = computed(() => emailDataStore.labelsLoading);

// 加载文件夹列表 - 使用 store 的缓存机制
const loadFolders = async () => {
  try {
    await emailDataStore.fetchFolders();
  } catch (error: any) {
    console.error('[EmailLeftNav] 加载文件夹失败:', error);
  }
};

// 加载标签列表 - 使用 store 的缓存机制
const loadTags = async () => {
  try {
    await emailDataStore.fetchLabels();
  } catch (error: any) {
    console.error('[EmailLeftNav] 加载标签失败:', error);
  }
};

// 监听路由变化，同步菜单选中状态
watch(
  [() => route.name, () => route.query.folderId, () => route.query.labelId],
  () => {
    initSelectedKeys();
  },
);

// 子菜单展开/收起事件
const handleOpenChange = (keys: string[]) => {
  openKeys.value = keys;
  
  // 当展开'我的文件夹'时，加载数据（store会自动判断是否使用缓存）
  if (keys.includes('myFolders')) {
    loadFolders();
  }
  
  // 当展开'我的标签'时，加载数据（store会自动判断是否使用缓存）
  if (keys.includes('myTags')) {
    loadTags();
  }
};

// 菜单选择事件
const handleMenuSelect = ({key}: any) => {
  console.log('[EmailLeftNav] 菜单选择:', key);
  selectedKeys.value = [key];
  
  // 启动进度条
  NProgress.start();
  
  // 如果是文件夹或标签，需要特殊处理
  if (key.startsWith('folder-')) {
    // 点击文件夹，打印文件夹ID并跳转到文件夹邮件页面
    const folderId = key.replace('folder-', '');
    console.log('点击文件夹，folderId:', folderId);
    router.push({
      name: 'EmailFolder',
      query: { folderId }
    });
  } else if (key.startsWith('tag-')) {
    // 点击标签，打印标签ID并跳转到标签邮件页面
    const labelId = key.replace('tag-', '');
    console.log('点击标签，labelId:', labelId);
    router.push({
      name: 'EmailLabel',
      query: { labelId }
    });
  } else {
    // 直接跳转到对应路由
    router.push({name: key});
  }
  
  // 路由跳转后结束进度条
  nextTick(() => {
    setTimeout(() => {
      NProgress.done();
    }, 300);
  });
};

// 写信按钮点击
const handleCompose = () => {
  console.log('[EmailLeftNav] 点击写信按钮');
  
  // 启动进度条
  NProgress.start();
  
  router.push('/emailMainPage/compose');
  
  // 路由跳转后结束进度条
  nextTick(() => {
    setTimeout(() => {
      NProgress.done();
    }, 300);
  });
};

// 清空垃圾箱
const handleClearTrash = () => {
  Modal.confirm({
    title: t('确认清空垃圾箱？'),
    content: t('清空后将永久删除垃圾箱中的所有邮件，此操作不可恢复！'),
    okText: t('确定'),
    cancelText: t('取消'),
    okType: 'danger',
    onOk: async () => {
      try {
        console.log('[EmailLeftNav] 开始清空垃圾箱');
        // 调用清空垃圾箱接口
        await emailApi.emptyTrash();
        message.success(t('垃圾箱已清空'));
        console.log('[EmailLeftNav] 清空垃圾箱成功');
        
        // 如果当前在垃圾箱页面，刷新列表
        if (route.name === 'EmailTrash') {
          console.log('[EmailLeftNav] 当前在垃圾箱页面，刷新列表');
          // 触发刷新事件，由垃圾箱页面监听
          window.dispatchEvent(new CustomEvent('email:trash-cleared'));
        }
      } catch (error: any) {
        console.error('[EmailLeftNav] 清空垃圾箱失败:', error);
        message.error(t('清空垃圾箱失败'));
      }
    },
  });
};

// 组件挂载时，仅初始化选中状态，不主动加载数据（数据在展开子菜单时按需加载）
onMounted(() => {
  console.log('[EmailLeftNav] onMounted - 初始化菜单选中状态');
  initSelectedKeys();
  
  // 不在这里预加载数据！
  // 改为：只有当前路由是文件夹或标签页面时，才加载对应数据
  if (route.name === 'EmailFolder' && route.query.folderId) {
    console.log('[EmailLeftNav] 当前在文件夹页面，加载文件夹数据');
    loadFolders();
  } else if (route.name === 'EmailLabel' && route.query.labelId) {
    console.log('[EmailLeftNav] 当前在标签页面，加载标签数据');
    loadTags();
  }
  // 其他情况，数据在用户展开子菜单时按需加载
});
</script>

<style type="text/css" lang="less" scoped>
.email-left-nav {
  width: 212px;
  background: linear-gradient(180deg, #fafbfc 0%, #f5f7fa 100%);
  display: flex;
  flex-direction: column;
  height: 100%;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.04);
  overflow-x: hidden;
  
  .nav-header {
    padding: 6px 14px;
    border-bottom: 1px solid #e8e8e8;
    background: #fff;
    
    .ant-btn {
      height: 33px;
      font-size: 15px;
      font-weight: 600;
      border-radius: 8px;
      background: linear-gradient(135deg, #1890ff 0%, #36cfc9 100%);
      border: none;
      box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(24, 144, 255, 0.4);
      }
      
      .remix-icon {
        margin-right: 8px;
        font-size: 18px;
      }
    }
  }
  
  .nav-menu {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 8px 0;
    
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #d9d9d9;
      border-radius: 3px;
      
      &:hover {
        background: #bfbfbf;
      }
    }
    
    :deep(.ant-menu) {
      border-right: none;
      background: transparent;
      
      .ant-menu-item {
        height: 44px;
        line-height: 44px;
        margin: 0;
        //margin: 4px 12px;
        border-radius: 8px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        
        &:hover {
          background-color: #ECF5FF;
        }
        
        &.ant-menu-item-selected {
          color: #409eff;
          font-weight: 500;
          background-color: #ECF5FF;
          &::after {
            display: none;
          }
        }
        
        .remix-icon {
          margin-right: 12px;
          font-size: 18px;
          transition: transform 0.3s;
        }
        
        &:hover .remix-icon {
          transform: scale(1.15);
        }
        
        // 垃圾箱清空图标
        .trash-clear-icon {
          float: right;
          font-size: 16px;
          color: #999;
          margin-right: 8px;
          transition: all 0.3s;
          cursor: pointer;
          
          &:hover {
            color: #ff4d4f;
            transform: scale(1.2);
          }
        }
        
        // 标签颜色点
        .tag-color-dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          margin-right: 8px;
          vertical-align: middle;
        }
        
        // 文件夹 badge
        .folder-badge {
          float: right;
          background: #ff4d4f;
          color: #fff;
          font-size: 12px;
          padding: 0 6px;
          border-radius: 10px;
          line-height: 20px;
          height: 20px;
          margin-top: 12px;
        }
        
        // 标签 badge
        .tag-badge {
          float: right;
          background: #52c41a;
          color: #fff;
          font-size: 12px;
          padding: 0 6px;
          border-radius: 10px;
          line-height: 20px;
          height: 20px;
          margin-top: 12px;
        }
      }
      
      // 子菜单样式
      .ant-menu-submenu {
        .ant-menu-submenu-title {
          height: 44px;
          line-height: 44px;
          border-radius: 8px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          
          &:hover {
            background-color: #ECF5FF;
          }
          
          .remix-icon {
            margin-right: 12px;
            font-size: 18px;
          }
        }
        
        // 子菜单项样式
        .ant-menu-sub {
          background: transparent !important;
          
          .ant-menu-item {
            padding-left: 48px !important;
            height: 36px;
            line-height: 36px;
            font-size: 13px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            
            &:hover {
              background: rgba(24, 144, 255, 0.1);
              transform: translateX(2px);
            }
            
            &.ant-menu-item-selected {
              background: rgba(24, 144, 255, 0.2);
              color: #1890ff;
              font-weight: 500;
            }
          }
        }
      }
    }
  }
}

// 加载动画
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
